import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "../../environments/environment";
import { BehaviorSubject, combineLatest, map, Observable, of, tap } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class CommonService {
  private apiUrl = environment.apiEndpoint;
  private roleUrl = environment.roleEndPoint;
  constructor(private http: HttpClient) {}

  private workTypesCache: any;
  private objectLevelsCache: any;
  private allCausesCache: any;
  private failureMechanismsCache: any;
  private allMaintainableItemsCache: any;

  private userFeaturePermissions = new BehaviorSubject<any>(null);
  private userRole = new BehaviorSubject<any>(null);
  private isRoleLoading = new BehaviorSubject<boolean>(false);

  userFeaturePermissions$ = this.userFeaturePermissions.asObservable();
  userRole$ = this.userRole.asObservable();
  isRoleLoading$ = this.isRoleLoading.asObservable();
  isTerminalUser$: Observable<boolean> = this.userRole$.pipe(
    tap((role) => console.log("User role in isTerminalUser$:", role)),
    // Map the role to a boolean indicating if the user is a terminal user
    map((role) => {
      const terminalUsers = [
        "RepairTechnician",
        "RepairManager",
        "RepairPlanner",
      ];
      return terminalUsers.includes(role);
    })
  );

  // Combined observable
  combined$ = combineLatest([this.userFeaturePermissions$, this.userRole$]);

  setuserFeaturePermissions(data: any) {
    this.userFeaturePermissions.next(data);
  }

  toggleRoleLoading(isLoading: boolean) {
    this.isRoleLoading.next(isLoading);
  }

  setUserRole(role: any) {
    this.userRole.next(role);
  }

  fetchRegions() {
    return this.http.get(`${this.apiUrl}/Filter/regions`);
  }

  fetchTerminalsByRegion(region: any) {
    let params = new HttpParams();
    if (region) {
      params = params.set("region", region);
    }
    return this.http.get(`${this.apiUrl}/TerminalManagement/getallterminals`, {
      params,
    });
  }

  getTerminalsByRegion(region: any) {
    let params = new HttpParams();
    if (region) {
      params = params.set("region", region);
    }
    return this.http.get(`${this.apiUrl}/Filter/getterminals`, {
      params,
    });
  }

  fetchTerminals() {
    return this.http.get(`${this.apiUrl}/filter/getterminals`);
  }

  fetchAllRegions() {
    return this.http.get(`${this.apiUrl}/Filter/regions`);
  }

  fetchObjectTypes(terminals = "") {
    return this.http.get(
      `${this.apiUrl}/Filter/getobjecttypes?terminalCode=${terminals}`
    );
  }

  fetchObjectIds(terminals = "", objectTypes = "") {
    return this.http.get(
      `${this.apiUrl}/Filter/getobjectids?terminalCode=${terminals}&objectType=${objectTypes}`
    );
  }

  fetchAssetTypes(terminalCode: string) {
    return this.http.get(
      `${this.apiUrl}/filter/getobjecttypes?terminalCode=${terminalCode}`
    );
  }

  // fetchWorkTypes() {
  //   return this.http.get(`${this.apiUrl}/filter/getworktypes`);
  // }

  // fetchObjectLevels() {
  //   return this.http.get(`${this.apiUrl}/filter/getobjectlevels`);
  // }

  // fetchAllCauses() {
  //   return this.http.get(`${this.apiUrl}/filter/getallcauses`);
  // }

  // fetchFailureMechanisms() {
  //   return this.http.get(`${this.apiUrl}/filter/getallfailuremechanisms`);
  // }

  // fetchAllMaintainableItems() {
  //   return this.http.get(`${this.apiUrl}/filter/getallmaintainableitems`);
  // }

  fetchAllWorkTypes() {
    return this.http.get(`${this.apiUrl}/filter/getworktypes`);
  }

  fetchObjectIDsFromAPI(filters: any, filter: any) {
    let objectTDsUrl = "assets/mock-data/object-ids-mockData.json";
    return this.http.get<any>(objectTDsUrl);
  }

  fetchMultilevelObjectIDs(objectTypes: any, objectLevels: any) {
    return this.http.get(
      `${this.apiUrl}/Filter/getobjectids?objectTypes=${objectTypes}&objectLevels=${objectLevels}`
    );
  }

  fetchRoles() {
    let name = "badactor";
    return this.http.get(
      `${this.roleUrl}/user/get-roles?applicationname=${name}`
    );
  }

  getUsers(terminal: any) {
    let appname = "badactor";
    return this.http.get(
      `${this.roleUrl}/user/get-user-list-by-terminal?terminals=${terminal}&applicationname=${appname}`
    );
  }

  fetchWorkTypes(forceRefresh: boolean = false): Observable<any> {
    if (this.workTypesCache && !forceRefresh) {
      return of(this.workTypesCache);
    }
    return this.http
      .get(`${this.apiUrl}/filter/getworktypes`)
      .pipe(tap((data: any) => (this.workTypesCache = data)));
  }

  fetchObjectLevels(forceRefresh: boolean = false): Observable<any> {
    if (this.objectLevelsCache && !forceRefresh) {
      return of(this.objectLevelsCache);
    }
    return this.http
      .get(`${this.apiUrl}/filter/getobjectlevels`)
      .pipe(tap((data) => (this.objectLevelsCache = data)));
  }

  fetchAllCauses(forceRefresh: boolean = false): Observable<any> {
    if (this.allCausesCache && !forceRefresh) {
      return of(this.allCausesCache);
    }
    return this.http
      .get(`${this.apiUrl}/filter/getallcauses`)
      .pipe(tap((data) => (this.allCausesCache = data)));
  }

  fetchFailureMechanisms(forceRefresh: boolean = false): Observable<any> {
    if (this.failureMechanismsCache && !forceRefresh) {
      return of(this.failureMechanismsCache);
    }
    return this.http
      .get(`${this.apiUrl}/filter/getallfailuremechanisms`)
      .pipe(tap((data) => (this.failureMechanismsCache = data)));
  }

  fetchAllMaintainableItems(forceRefresh: boolean = false): Observable<any> {
    if (this.allMaintainableItemsCache && !forceRefresh) {
      return of(this.allMaintainableItemsCache);
    }
    return this.http
      .get(`${this.apiUrl}/filter/getallmaintainableitems`)
      .pipe(tap((data) => (this.allMaintainableItemsCache = data)));
  }

  fetchUserFeaturePersmissions() {
    let appname = "badactor";
    return this.http.get(
      `${this.roleUrl}/user/get-users-permissions-features?applicationname=${appname}`
    );
  }
}
